import { get, isEmpty } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { selectEligibleForEscrowScheduled, selectLoanNumberSelected } from '@store/selectors/widgets.selectors';
import { AlertBase, BoxBase, StackBase } from '@fmc-digital/ui';
import React, { useEffect, useState } from 'react';
import { Notifications, WidgetTemplate } from '@types';
import { AlertColor, Link, useMediaQuery, useTheme } from '@mui/material';
import { filterAlertsifEscrowScheduledExsists } from '@utils/filterWidgets';
import { filterAlertsBystatus } from '@utils/getStatusBasedBanners';
import { SelectNotificationsData } from '@store/selectors/alerts.selectors';
import { selectActiveAccountInformation } from '@store/selectors/accounts.selector';
import { accountStatusEvents, CodingConstants } from '@constants';
import { logUserEvent } from '@analytics/amplifyLogging';
import { v4 } from 'uuid';

export const SiteMessages = ({ handleLinkClick }: any) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [closedNotifiIds, setClosedNotifiIds] = React.useState([]);
    const selectActiveAccount = useSelector(selectActiveAccountInformation);
    const accountStatus = selectActiveAccount?.accountStatus?.name;
    const accountStatusNameKey = selectActiveAccount?.accountStatus?.nameKey;
    const notifications = useSelector(SelectNotificationsData);

    const escrowScheduled: WidgetTemplate = useSelector(selectEligibleForEscrowScheduled);
    const loanNbr = useSelector(selectLoanNumberSelected);

    const data = React.useMemo(() => {
        let merged: Notifications[] = notifications || [];
        if (!isEmpty(escrowScheduled)) {
            merged = filterAlertsifEscrowScheduledExsists(escrowScheduled, merged, loanNbr, 'info');
        }
        if (accountStatus) {
            const statusBasedAlerts: any = filterAlertsBystatus(accountStatus, accountStatusNameKey);
            if (statusBasedAlerts) {
                merged = [...merged, statusBasedAlerts];
            }
        }

        return isEmpty(merged)
            ? undefined
            : merged.reduce(
                  (result: { left: Notifications[]; right: Notifications[] }, current: Notifications, index: number) => {
                      if (index % 2 === 0) return { ...result, left: result.left.concat(current) };
                      return { ...result, right: result.right.concat(current) };
                  },
                  { left: [], right: [] }
              );
    }, [notifications, escrowScheduled, loanNbr, accountStatus, accountStatusNameKey]);

    if (!data) return null;

    const { left, right } = get({ data }, ['data'], { left: [], right: [] });

    const createLearnMoreLink = (notification: Notifications) => {
        let learnMoreLink;
        if (notification?.link) {
            if (notification.link === '#') {
                learnMoreLink = (
                    <Link
                        style={{ cursor: 'pointer', fontWeight: 500 }}
                        onClick={() => {
                            handleLinkClick(true);
                            logUserEvent({
                                ...accountStatusEvents.logEvent.LearnMoreLink,
                                uuid: v4()
                            });
                        }}
                    >
                        Learn More
                    </Link>
                );
            } else {
                learnMoreLink = (
                    <Link style={{ fontWeight: 500 }} href={notification.link} rel="noopener noreferrer" target="_blank">
                        Learn More
                    </Link>
                );
            }
        }
        return learnMoreLink;
    };

    return (
        <>
            <StackBase direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, lg: 4 }} mb={{ sm: 4 }} justifyContent="space-between">
                {isEmpty(left) ? (
                    isMobile ? null : (
                        <BoxBase sx={{ width: { xs: '22.375rem', sm: '24.975rem', lg: '39.6875rem' } }} />
                    )
                ) : (
                    <StackBase gap={{ xs: 2, lg: 4 }} width={{ lg: '49%' }}>
                        {left
                            .filter(({ notificationId }) => (isEmpty(closedNotifiIds) ? true : closedNotifiIds.indexOf(notificationId) < 0))
                            .map((notifi: Notifications) => {
                                const severity = get(notifi, ['template', 'templateIcon'], '');
                                if (isEmpty(severity)) console.error('templateIcon ', notifi?.template?.templateIcon);
                                let color = 'info';
                                if (['warning', 'info', 'error', 'success'].some(colr => colr === severity)) color = severity;
                                else console.error('severity not found ', severity);
                                return (
                                    <AlertBase
                                        key={notifi.template.templateId}
                                        variant="standard"
                                        severity={color as AlertColor}
                                        sx={{
                                            maxWidth: { sm: '22.375rem', lg: '100%' },
                                            alignItems: 'center',
                                            '& .MuiAlert-action': { alignSelf: 'flex-start' }
                                        }}
                                        onClose={() => setClosedNotifiIds(prevState => [...prevState, notifi.notificationId])}
                                    >
                                        {notifi.subject && `${notifi.subject}\u00A0`}
                                        {notifi.content}&nbsp;
                                        {createLearnMoreLink(notifi)}
                                    </AlertBase>
                                );
                            })}
                    </StackBase>
                )}
                {isEmpty(right) ? (
                    isMobile ? null : (
                        <BoxBase sx={{ width: { xs: '22.375rem', sm: '24.975rem', lg: '39.6875rem' } }} />
                    )
                ) : (
                    <StackBase gap={{ xs: 2, lg: 4 }} width={{ lg: '49%' }}>
                        {right
                            .filter(({ notificationId }) => (isEmpty(closedNotifiIds) ? true : closedNotifiIds.indexOf(notificationId) < 0))
                            .map((notifi: Notifications) => {
                                const severity = get(notifi, ['template', 'templateIcon'], '');
                                if (isEmpty(severity)) console.error('templateIcon ', notifi?.template?.templateIcon);
                                let color = 'info';
                                if (['warning', 'info', 'error', 'success'].some(colr => colr === severity)) color = severity;
                                else console.error('severity not found ', severity);
                                return (
                                    <AlertBase
                                        key={notifi.template.templateId}
                                        variant="standard"
                                        severity={color as AlertColor}
                                        sx={{
                                            maxWidth: { sm: '22.375rem', lg: '100%' },
                                            alignItems: 'center',
                                            '& .MuiAlert-action': { alignSelf: 'flex-start' }
                                        }}
                                        onClose={() => setClosedNotifiIds(prevState => [...prevState, notifi.notificationId])}
                                    >
                                        {notifi.subject && `${notifi.subject}\u00A0`}
                                        {notifi.content}&nbsp;
                                        {createLearnMoreLink(notifi)}
                                    </AlertBase>
                                );
                            })}
                    </StackBase>
                )}
            </StackBase>
        </>
    );
};
